import { z } from 'zod';
import { ACCOUNT_KINDS, BILL_OPERATION_TYPES, NOTIFICATION_KINDS, OCR_STATUSES, PAYMENT_FLOWS, RATE_SOURCES, RENEWAL_PERIODS, REPORT_STATUSES, SUBSCRIPTION_STATUSES, TRANSACTION_TYPES, } from './enums';
/**
 * Zod schemas for the PLAN.md §5 domain model.
 *
 * Conventions:
 *  - ids are cuid strings
 *  - timestamps arrive as ISO strings and are coerced to `Date`
 *  - money is a plain `number` at the transport edge; Prisma stores `Decimal`
 *
 * SECURITY: `Credential` only ever carries *ciphertext* fields (`*Enc`).
 * Plaintext credentials are produced solely by the explicit, audit-logged
 * decryption endpoint and are never part of these shared schemas.
 */
const id = z.string().min(1);
const isoDate = z.coerce.date();
const nullableIsoDate = z.coerce.date().nullable();
const money = z.number();
const nullableMoney = z.number().nullable();
/** AES-256-GCM ciphertext, base64 (see PLAN.md §8). */
const ciphertext = z.string().min(1);
export const SubscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export const PaymentFlowSchema = z.enum(PAYMENT_FLOWS);
export const TransactionTypeSchema = z.enum(TRANSACTION_TYPES);
export const RateSourceSchema = z.enum(RATE_SOURCES);
export const AccountKindSchema = z.enum(ACCOUNT_KINDS);
export const NotificationKindSchema = z.enum(NOTIFICATION_KINDS);
export const ReportStatusSchema = z.enum(REPORT_STATUSES);
export const RenewalPeriodSchema = z.enum(RENEWAL_PERIODS);
export const OcrStatusSchema = z.enum(OCR_STATUSES);
/** VendorGroup — groups multiple instances/buyouts of one vendor ("Z.ai"). */
export const VendorGroupSchema = z.object({
    id,
    name: z.string().min(1),
    note: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** Assigned users of a subscription (email list). */
export const SubscriptionUserSchema = z.object({
    id,
    subscriptionId: id,
    email: z.string().email(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** Ciphertext only — never send or store plaintext here. */
export const CredentialSchema = z.object({
    id,
    subscriptionId: id,
    loginEnc: ciphertext,
    passwordEnc: ciphertext,
    extraEnc: ciphertext.nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** Freedom USD …848 / Sberbank / Cifra / Platipomiru … */
export const MoneyAccountSchema = z.object({
    id,
    kind: AccountKindSchema,
    name: z.string().min(1),
    currency: z.string().min(3).max(3),
    meta: z.record(z.string(), z.unknown()).nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** One *logical* payment (may consist of several bank transactions). */
export const PaymentEventSchema = z.object({
    id,
    subscriptionId: id,
    occurredAt: isoDate,
    flow: PaymentFlowSchema,
    paidAmount: money,
    paidCurrency: z.string().min(3).max(3),
    rubTotal: nullableMoney,
    note: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** A single bank/card transaction; `sourceHash` dedups imports. */
export const TransactionSchema = z.object({
    id,
    paymentEventId: id.nullable(),
    subscriptionId: id.nullable(),
    accountId: id,
    type: TransactionTypeSchema,
    occurredAt: isoDate,
    amount: money,
    currency: z.string().min(3).max(3),
    fxRate: nullableMoney,
    rateSource: RateSourceSchema.nullable(),
    rubAmount: nullableMoney,
    commission: nullableMoney,
    sourceTxnNo: z.string().nullable(),
    fileKey: z.string().nullable(),
    fileSha256: z.string().nullable(),
    ocrStatus: OcrStatusSchema.nullable(),
    ocrJson: z.record(z.string(), z.unknown()).nullable(),
    sourceHash: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** In-app bell entry (expiring ≤14d, upcoming renewals, status changes). */
export const NotificationSchema = z.object({
    id,
    kind: NotificationKindSchema,
    subscriptionId: id.nullable(),
    dueAt: nullableIsoDate,
    readAt: nullableIsoDate,
    payload: z.record(z.string(), z.unknown()).nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** Reimbursement report ("pay me back") run. */
export const ReportSchema = z.object({
    id,
    periodFrom: isoDate,
    periodTo: isoDate,
    status: ReportStatusSchema,
    totalsJson: z.record(z.string(), z.unknown()).nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
export const SubscriptionSchema = z.object({
    id,
    groupId: id.nullable(),
    name: z.string().min(1),
    url: z.string().url().nullable(),
    purpose: z.string().nullable(),
    addedAt: isoDate,
    initialCost: money,
    renewalCost: money,
    renewalPeriod: RenewalPeriodSchema,
    currency: z.string().min(3).max(3),
    expiresAt: isoDate,
    expiresHighlightAfter: nullableIsoDate,
    status: SubscriptionStatusSchema,
    blockedReason: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/** One uploaded bill object (PDF / screenshot) in Yandex Object Storage. */
export const BillFileSchema = z.object({
    id,
    fileKey: z.string().min(1),
    fileSha256: z.string().length(64),
    mime: z.string().min(1),
    size: z.number().int().nonnegative(),
    originalName: z.string().nullable(),
    transactionId: id.nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
/**
 * What the LLM must return for a bill (Russian bank receipt, PLAN.md §4).
 * `date` is a plain `YYYY-MM-DD` string, not an ISO datetime, because the
 * receipt carries a date only — the wall-clock part lives in `time`.
 * Optional fields are `null` when the receipt simply does not show them.
 */
export const BillExtractionSchema = z.object({
    vendor: z.string().min(1),
    serviceHint: z.string().min(1),
    amount: z.number().positive(),
    currency: z.string().min(3).max(3).transform((value) => value.toUpperCase()),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
    time: z
        .string()
        .regex(/^\d{2}:\d{2}(:\d{2})?$/, 'time must be HH:MM or HH:MM:SS')
        .nullable()
        .optional(),
    txnNo: z.string().min(1),
    cardLast4: z
        .string()
        .regex(/^\d{4}$/, 'cardLast4 must be exactly 4 digits')
        .nullable()
        .optional(),
    operationType: z.enum(BILL_OPERATION_TYPES),
    operationTypeRaw: z.string().nullable().optional(),
    fxRate: z.number().positive().nullable().optional(),
    commission: z.number().nullable().optional(),
});
/**
 * PATCH /bills/:id/confirm body — human overrides merged into the linked
 * transaction before the bill is marked CONFIRMED.
 */
export const BillConfirmSchema = z.object({
    amount: z.number().positive().optional(),
    currency: z.string().min(3).max(3).optional(),
    occurredAt: isoDate.optional(),
    fxRate: z.number().positive().nullable().optional(),
    rateSource: RateSourceSchema.nullable().optional(),
    commission: z.number().nullable().optional(),
    sourceTxnNo: z.string().nullable().optional(),
    subscriptionId: id.nullable().optional(),
});
export const ALL_SCHEMAS = {
    VendorGroup: VendorGroupSchema,
    Subscription: SubscriptionSchema,
    SubscriptionUser: SubscriptionUserSchema,
    Credential: CredentialSchema,
    MoneyAccount: MoneyAccountSchema,
    PaymentEvent: PaymentEventSchema,
    Transaction: TransactionSchema,
    Notification: NotificationSchema,
    Report: ReportSchema,
    BillFile: BillFileSchema,
};
//# sourceMappingURL=schemas.js.map